/**
 * Factory for the read-only polling hooks behind the lifecycle books and the
 * Client Success roster surfaces.
 *
 * Each of those hooks was byte-identical modulo four knobs: a query key, an
 * endpoint path, a response schema, and a poll interval. The shape is always
 * the same — `useQuery` → `apiGet` → `schema.safeParse` → throw a
 * schema-validation Error on failure → return the parsed data, with
 * `refetchInterval` set. This factory captures that shape once; each
 * `use-*.ts` file stays a thin wrapper that calls it with its own knobs (and
 * keeps its own doc comment, which carries the per-surface poll-interval
 * rationale).
 *
 * Hooks with extra options (auth posture, `enabled` gating, parameterised
 * paths, mutations) do NOT come through here — they keep their bespoke
 * `useQuery` call.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { ZodType, ZodTypeDef } from "zod";
import { apiGet } from "./api";

/**
 * Build a read-only polling hook. The returned hook takes no arguments and
 * polls `path` every `refetchIntervalMs`, validating each response through
 * `schema` and throwing a schema-validation Error (the same message the
 * hand-written hooks raised) when the payload doesn't match.
 */
export function createPollingQuery<T, I = T>({
  queryKey,
  path,
  schema,
  refetchIntervalMs,
}: {
  queryKey: readonly unknown[];
  path: string;
  /** Input type carried as its own parameter so schemas with `.transform()`
   *  fields (output ≠ input, e.g. FirmThreadSchema's firm_id normalisation)
   *  bind T to the OUTPUT — plain `ZodType<T>` would reject or mis-infer them. */
  schema: ZodType<T, ZodTypeDef, I>;
  refetchIntervalMs: number;
}): () => UseQueryResult<T, Error> {
  return function usePollingQuery() {
    return useQuery<T, Error>({
      queryKey,
      queryFn: async () => {
        const raw = await apiGet<unknown>(path);
        const parsed = schema.safeParse(raw);
        if (!parsed.success) {
          throw new Error(
            `${path} failed schema validation: ${parsed.error.message}`,
          );
        }
        return parsed.data;
      },
      refetchInterval: refetchIntervalMs,
    });
  };
}
